import { SpaceRelation, SpaceType } from './SpaceAssignments';
import type { AuthApiRole } from './AssignSpaceAfterRoleChangeModal';

/**
 * Pure orchestrator extracted from `AssignSpaceAfterRoleChangeModal.handleConfirm`.
 *
 * Encapsulates the two-step Admin/Owner→Member downgrade — commit role,
 * then assign spaces — plus the rollback-on-all-spaces-fail recovery
 * path. Pure so the rollback logic can be unit-tested without driving
 * Antd's TreeSelect from RTL.
 *
 * Five outcomes, one per branch the modal callbacks need to react to:
 *
 *   - `role_update_failed`                — step 1 rejected; nothing committed.
 *   - `full_success`                      — step 1 + every step 2 assign succeeded.
 *   - `partial_success`                   — step 1 succeeded, some assigns failed.
 *   - `all_spaces_failed_role_reverted`   — step 1 succeeded, every assign failed,
 *                                            and the rollback (step 1 with originalRoles)
 *                                            succeeded. Modal can close as a true cancel.
 *   - `all_spaces_failed_revert_also_failed` — step 1 succeeded, every assign failed,
 *                                            AND the rollback failed. The user is now
 *                                            a Member with zero spaces and no automatic
 *                                            recovery — admin must clean up manually.
 *
 * Known edge case — phantom assigns: if the auth-API commits an OpenFGA
 * tuple write server-side but the response surfaces as a rejected
 * mutation (e.g., 5xx after the write), the orchestrator treats that
 * assign as "failed" for purposes of the rollback decision. After
 * rollback the user ends up at the original role *plus* the phantom
 * grant. This is recoverable (admin can remove the orphan grant from the
 * Space Assignments view) and rare in practice, but worth knowing about.
 * Mitigating it correctly requires backend idempotency keys, which is a
 * separate workstream.
 */
export interface DowngradeOrchestratorParams {
  userId: string;
  rolesToAssign: AuthApiRole[];
  originalRoles: AuthApiRole[];
  spacesToAssign: Array<{ id: string; level: SpaceType }>;
  relation: SpaceRelation;
  updateUser: (input: { id: string; roles: AuthApiRole[] }) => Promise<unknown>;
  assignToSpace: (input: {
    user_id: string;
    space_type: SpaceType;
    space_id: string;
    relation: SpaceRelation;
  }) => Promise<unknown>;
  callbacks: {
    onRoleUpdateFailed: (error: unknown) => void;
    onFullSuccess: () => void;
    onPartialSuccess: (failedCount: number, totalCount: number) => void;
    onAllSpacesFailedRoleReverted: () => void;
    onAllSpacesFailedRevertAlsoFailed: (revertError: unknown) => void;
  };
}

export type DowngradeOutcome =
  | 'role_update_failed'
  | 'full_success'
  | 'partial_success'
  | 'all_spaces_failed_role_reverted'
  | 'all_spaces_failed_revert_also_failed';

export async function runDowngradeWithSpaces(
  params: DowngradeOrchestratorParams,
): Promise<DowngradeOutcome> {
  const {
    userId,
    rolesToAssign,
    originalRoles,
    spacesToAssign,
    relation,
    updateUser,
    assignToSpace,
    callbacks,
  } = params;

  // Step 1: commit the role change.
  try {
    await updateUser({ id: userId, roles: rolesToAssign });
  } catch (err) {
    callbacks.onRoleUpdateFailed(err);
    return 'role_update_failed';
  }

  // Step 2: assign all spaces in parallel via Promise.allSettled so a
  // single failure doesn't short-circuit the rest.
  const results = await Promise.allSettled(
    spacesToAssign.map((space) =>
      assignToSpace({
        user_id: userId,
        space_type: space.level,
        space_id: space.id,
        relation,
      }),
    ),
  );
  const failedCount = results.filter((r) => r.status === 'rejected').length;

  if (failedCount === spacesToAssign.length) {
    // Every assign failed. The user is currently a Member with zero
    // spaces — exactly the access-loss state the flow is meant to
    // prevent. Try to revert the role mutation. If revert succeeds the
    // downgrade has effectively been undone.
    try {
      await updateUser({ id: userId, roles: originalRoles });
      callbacks.onAllSpacesFailedRoleReverted();
      return 'all_spaces_failed_role_reverted';
    } catch (revertErr) {
      callbacks.onAllSpacesFailedRevertAlsoFailed(revertErr);
      return 'all_spaces_failed_revert_also_failed';
    }
  }

  if (failedCount > 0) {
    callbacks.onPartialSuccess(failedCount, spacesToAssign.length);
    return 'partial_success';
  }

  callbacks.onFullSuccess();
  return 'full_success';
}
